import React from 'react';
import dynamic from 'next/dynamic';
const Popup = dynamic(() => import('reactjs-popup'));
import cn from 'classnames';

import { formatCurrency } from '../../utils';

import css from './salaryChart.module.css';

const BAR_HEIGHT_MODIFIER = 70;

interface SalaryChartProps {
  chartAttributes: {
    colors: string[];
    median?: number;
    percentiles: string[];
    // Bellow props should be one or the other, but typescript is hard.
    horizontalPos?: number[];
    horizontal_pos?: number[];
    verticalPos?: number[];
    vertical_pos?: number[];
  };
  showTooltip?: boolean;
  containerClassName?: string;
  defaultMedianPos?: 'bottom' | 'middle' | 'top';
  defaultLabelPos?: 'bottom' | 'middle' | 'top';
  maxWidth?: string;
  minWidth?: string;
  barWidth?: string;
}

const SalaryChart = (props: SalaryChartProps) => {
  const {
    chartAttributes,
    containerClassName,
    defaultMedianPos = 'bottom',
    defaultLabelPos = 'bottom',
    maxWidth = '100%',
    minWidth = '100%',
    showTooltip = true,
  } = props;

  // The medianIdx is the idx where the medianValue should replace the value in horizontalPos.
  const medianIdx = chartAttributes.colors.findIndex((color) => color === '#26A69A');
  const medianValue = chartAttributes.median || chartAttributes.percentiles[1];
  const horizontalPos = chartAttributes.horizontalPos || chartAttributes.horizontal_pos || [];
  const verticalPos = chartAttributes.verticalPos || chartAttributes.vertical_pos || [];

  const barStyle = (idx: number) => ({
    height: `${Math.floor(verticalPos[idx] * BAR_HEIGHT_MODIFIER)}px`,
    backgroundColor:
      chartAttributes.colors[idx] === '#BAC4DA'
        ? 'rgba(186, 196, 218, 0.3)'
        : chartAttributes.colors[idx],
    borderBottom: `3px solid ${chartAttributes.colors[idx] === '#BAC4DA' ? '#BAC4DA' : '#26A69A'}`,
    transition: 'height 500ms ease-in-out',
    gridRow: '2/3',
    gridColumn: `${idx + 1}/${idx + 2}`,
  });

  const VerticalBarAndLabel = (props) => {
    const {
      labelTitle,
      labelSubtitle,
      tooltipSubtitle,
      showLabel,
      barStyle,
      labelStyle,
      labelClassName,
      _showTooltip,
    } = props;

    return (
      <>
        {/**
         * The tooltip component wraps the vertical bar element because that's how the
         * reactjs-popup works :shrug:
         * The bar is still rendered when the tooltip is disabled.
         */}
        <Popup
          trigger={
            // This div is the _actual_ vertical BAR.
            <div style={barStyle} />
          }
          disabled={!_showTooltip}
          position="top center"
          on={['hover', 'focus']}
          contentStyle={{
            borderRadius: '4px',
            backgroundColor: 'rgba(51, 51, 51, 0.95)',
            boxShadow: '0 0 25px rgba(0, 0, 0, 0.05)',
          }}
          arrowStyle={{
            color: 'rgba(51, 51, 51, 0.95)',
          }}
        >
          <div className="d-flex flex-column font-14 text-white z-p-8">
            <span className="font-weight-600">{labelTitle}</span>
            <span>{tooltipSubtitle}</span>
          </div>
        </Popup>

        {showLabel && (
          <label
            className={cn('d-flex flex-column font-14 mb-0', labelClassName)}
            style={{
              width: 'max-content',
              lineHeight: 'initial',
              ...labelStyle,
            }}
          >
            <span className="font-weight-600">{labelTitle}</span>
            <span>{labelSubtitle}</span>
          </label>
        )}
      </>
    );
  };

  return (
    <div
      className={cn('d-grid align-items-end', css['chart-container'], containerClassName)}
      style={{ maxWidth, minWidth }}
    >
      {horizontalPos.map((value, idx) => (
        <VerticalBarAndLabel
          // The magic numbers are array indexes DEPENDENT on horizontalPos.length.
          showLabel={[0, horizontalPos.length - 1].includes(idx) || idx === medianIdx}
          labelTitle={formatCurrency(idx === medianIdx ? medianValue : value)}
          labelSubtitle={idx === medianIdx ? 'Median' : `${(idx + 1) * 10}%`}
          tooltipSubtitle={`${(idx + 1) * 10}%`}
          barStyle={barStyle(idx)}
          labelStyle={{
            // Math.ceil(idx / 7), for 9 indexes, results in: [0, 1, 1, 1, 1, 1, 1, 1, 2]
            gridColumnStart: [idx + 1, idx, ''][Math.ceil(idx / 7)],
            gridColumnEnd: ['', idx + 3, idx + 2][Math.ceil(idx / 7)],
            gridRow: idx == medianIdx ? defaultMedianPos : defaultLabelPos,
            justifySelf: ['flex-start', 'center', 'flex-end'][Math.ceil(idx / 7)],
          }}
          labelClassName={['text-left', 'text-center', 'text-right'][Math.ceil(idx / 7)]}
          _showTooltip={showTooltip}
          key={`salary-chart-bar-${idx}`}
        />
      ))}
    </div>
  );
};

export default SalaryChart;
